import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import imageCompression from 'browser-image-compression';
import { todayLocal } from '../lib/dateHelpers';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import LastSavedIndicator from '../components/LastSavedIndicator';
import { 
  Save, 
  Send, 
  Plus, 
  Trash2, 
  Camera, 
  MapPin, 
  Loader2,
  ArrowLeft,
  FileText,
  Image,
  X,
  AlertCircle,
  AlertTriangle,
  Download,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  computeFieldReportPerms,
  photoReadinessLabel,
} from '../utils/fieldReportWorkflow';

const API = process.env.REACT_APP_BACKEND_URL;
import { resolveFileUrl } from '../lib/fileUtils';
import { SafeImage } from '../components/SafeImage';
import JobSelectionCard from '../components/JobSelectionCard';
import { uploadFieldReportPhoto, uploadFieldReportSignature } from '../lib/fieldReportUploads';

// Signature pad component (reused from SoftDigForm)
function SignaturePad({ label, signatureUrl, onSign, gpsData, name, onNameChange, signedAt, required, disabled, onUploadSignature }) {
  const sigRef = useRef(null);
  const [showPad, setShowPad] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const captureSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Please sign before capturing');
      return;
    }

    setCapturing(true);

    // Read the canvas FIRST, before requesting GPS. The one-time native
    // "Allow location?" browser prompt sits directly over this button
    // row and a leaked dismiss-tap can land on "Clear" — reading the
    // signature now (and disabling Clear below) closes that timing gap.
    const dataUrl = sigRef.current.toDataURL('image/png');

    let gps = { lat: null, lng: null, accuracy: null };
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { 
          enableHighAccuracy: true, 
          timeout: 10000 
        });
      });
      gps = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      };
    } catch (err) {
      console.warn('GPS not available:', err);
    }

    try {
      const uploadedUrl = onUploadSignature ? await onUploadSignature(dataUrl) : dataUrl;
      if (!uploadedUrl) throw new Error('Signature upload failed');
      onSign(uploadedUrl, gps);
      setShowPad(false);
    } catch (err) {
      console.error('Signature upload error:', err);
      toast.error('Failed to upload signature');
    } finally {
      setCapturing(false);
    }
  };

  const clearSignature = () => {
    // Second safety net: once capture has started, the canvas has
    // already been read — never let a leaked tap wipe it mid-upload.
    if (capturing) return;
    if (sigRef.current) {
      sigRef.current.clear();
    }
  };

  return (
    <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
      <Label className="text-sm font-semibold">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      
      <div className="space-y-2">
        <Input
          placeholder="Name"
          value={name || ''}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={disabled}
        />
        
        {signedAt && (
          <p className="text-xs text-slate-500">
            Signed: {new Date(signedAt).toLocaleString()}
          </p>
        )}
        
        {/* GPS display removed per user request */}
      </div>

      {signatureUrl ? (
        <div className="border rounded-lg p-2 bg-white">
          <img src={signatureUrl} alt="Signature" className="max-h-24 mx-auto" />
          {!disabled && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full mt-2"
              onClick={() => {
                onSign(null, null);
                setShowPad(true);
              }}
            >
              Re-sign
            </Button>
          )}
        </div>
      ) : (
        <>
          {showPad ? (
            <div className="space-y-2">
              <div className="border-2 border-slate-300 rounded-lg bg-white touch-none">
                <SignatureCanvas
                  ref={sigRef}
                  canvasProps={{
                    className: 'w-full h-32',
                    style: { width: '100%', height: '128px' }
                  }}
                  backgroundColor="white"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={clearSignature} disabled={capturing} className="flex-1">
                  Clear
                </Button>
                <Button type="button" size="sm" onClick={captureSignature} disabled={capturing} className="flex-1 bg-green-600 hover:bg-green-700">
                  {capturing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Capture'}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setShowPad(true)}
              disabled={disabled}
            >
              <FileText className="w-4 h-4 mr-2" />
              Tap to Sign
            </Button>
          )}
        </>
      )}
    </div>
  );
}

// Scouting Photo Card
function ScoutingPhotoCard({ photo, onRemove, onCaptionChange, disabled }) {
  return (
    <div className="relative border rounded-lg overflow-hidden bg-white">
      <SafeImage
        src={resolveFileUrl(photo)}
        alt="Scouting"
        // object-contain so portrait + landscape photos show their full
        // content; bg-slate-50 letterboxes the unused space. Matches the
        // LocatePhotoCard styling.
        className="w-full h-64 object-contain bg-slate-50"
        // Critical for forms with 10+ photos: defer below-the-fold image
        // fetches until they scroll into view, and decode off the main
        // thread. Without these, every photo fired a OneDrive proxy
        // round-trip on mount, queueing behind the browser's 6-per-host
        // concurrency limit and stalling paint.
        loading="lazy"
        decoding="async"
      />
      {!disabled && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 bg-white/80 hover:bg-white"
          onClick={onRemove}
        >
          <X className="w-4 h-4 text-red-500" />
        </Button>
      )}
      <div className="p-2">
        <Textarea
          placeholder="Add caption..."
          value={photo.caption || ''}
          onChange={(e) => onCaptionChange(e.target.value)}
          className="text-sm resize-y min-h-[44px]"
          rows={2}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

// Main PreCon Form Component
export default function PreConForm() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, isAdmin } = useAuth();
  const isEditing = !!id;
  const isViewOnlyParam = searchParams.get('view') === 'true';

  const [loading, setLoading] = useState(false);
  // True when loadPreCon hit a network/timeout/5xx error (not a real
  // 404/403) — shows an inline Retry instead of booting the tech back to
  // the Field Reports list on a bad-signal blip (2026-09-01, same fix as
  // SoftDigForm.js).
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // See LocateForm: mirrors backend expected_photos_count / received / ready.
  const [pendingUploads, setPendingUploads] = useState(0);
  const fileInputRef = useRef(null);
  // Track id locally so newly-created drafts have an id for photo uploads
  const [currentId, setCurrentId] = useState(id || null);

  // Job selection state
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Support both 'job' and 'job_id' query params for flexibility
  const jobIdFromParams = searchParams.get('job') || searchParams.get('job_id') || '';

  const [formData, setFormData] = useState({
    job_id: jobIdFromParams,
    job_number: '',
    assigned_team: '',
    site_location: '',
    visit_date: todayLocal(),
    cll_lead_on_site: user?.full_name || '',
    crew_members: '',
    project_name: '',
    client_owner: '',
    project_number: '',
    scope_overview: '',
    access_notes: '',
    safety_notes: '',
    utility_awareness_notes: '',
    additional_notes: '',
    scouting_photos: [],
    submitted_by: user?.full_name || '',
    submitter_name: user?.full_name || '',
    submitter_signed_at: '',
    submitter_signature_url: '',
    submitter_gps_lat: null,
    submitter_gps_lng: null,
    submitter_gps_accuracy: null,
    reviewer_name: '',
    reviewer_signed_at: '',
    reviewer_signature_url: '',
    reviewer_gps_lat: null,
    reviewer_gps_lng: null,
    reviewer_gps_accuracy: null,
    status: 'draft'
  });

  // Workflow permissions — mirrors backend rules in
  // /utils/field_report_workflow.py.
  const canApproveFieldReports = () => {
    if (isAdmin()) return true;
    const permissions = user?.permissions || {};
    return permissions.can_approve_field_reports === true;
  };

  const {
    isCompleted,
    isPending,
    isSubmitter,
    isAdminOrSuper,
    isViewOnly,
    submitterSignatureLocked,
    canAdminUnlock,
    isAdminEditingNonDraft,
  } = computeFieldReportPerms({
    formData,
    user,
    viewParamSet: isViewOnlyParam,
    canApprove: canApproveFieldReports(),
  });

  // Unlock the form so admin/approver can fix a mistake. Idempotent —
  // calling it on an already-unlocked form is a no-op.
  const handleAdminEdit = () => {
    if (!isViewOnly) return;
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next, { replace: true });
  };

  // Admin's "Save Changes" button on an unlocked approved/pending report.
  // Reuses handleSaveSubmitterEdits which PUTs the record WITHOUT touching
  // status, so an Approved report stays Approved after a typo fix.
  const handleAdminSaveEdits = async () => {
    await handleSaveSubmitterEdits();
  };

  const canAdminEdit = canApproveFieldReports() && isPending;
  const isSubmittedOrCompleted = isPending || isCompleted;
  const photoBannerText = photoReadinessLabel({
    expected: formData.expected_photos_count,
    received: formData.received_photos_count,
    photos_ready: formData.photos_ready,
  });
  const photosNotReady = pendingUploads > 0 || photoBannerText !== null;

  // Autosave timer
  const autosaveRef = useRef(null);
  // SAFETY: see LocateForm.js — block save-draft / autosave until the
  // server's data has actually been loaded into formData.
  const formLoadedRef = useRef(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Load available jobs on mount
  useEffect(() => {
    loadAvailableJobs();
  }, [token]);

  // Always start the form scrolled to the top (fixes "opens at bottom"
  // bug when navigating in from the bottom of a Job detail page).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Auto-select job if job_id comes from URL params (when creating new report from Job page).
  // Fall back to a single targeted fetch if the job isn't in the active-job dropdown
  // (e.g., status = "Ready to schedule" or any non-active status the form would otherwise skip).
  useEffect(() => {
    if (isEditing || !jobIdFromParams || selectedJob || loadingJobs) return;
    const inList = availableJobs.find(j => j.id === jobIdFromParams);
    if (inList) {
      handleJobSelect(jobIdFromParams);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/jobs/${jobIdFromParams}/field-report-prefill`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled || !res.data?.id) return;
        const job = res.data;
        setAvailableJobs(prev => prev.some(j => j.id === job.id) ? prev : [...prev, job]);
        setSelectedJob(job);
        setFormData(prev => ({
          ...prev,
          job_id: job.id,
          job_number: job.job_number,
          site_location: job.service_address || '',
          assigned_team: job.assigned_team_names?.join(', ') || '',
          visit_date: job.date || prev.visit_date,
          cll_lead_on_site: user?.full_name || prev.cll_lead_on_site,
          project_name: job.job_title || '',
          client_owner: job.client_name || '',
          submitted_by: user?.full_name || prev.submitted_by,
          submitter_name: user?.full_name || prev.submitter_name,
          crew_members: prev.crew_members || (job.assigned_team_names?.join(', ') || ''),
        }));
      } catch (err) {
        console.warn('Prefill fetch failed for job', jobIdFromParams, err);
      }
    })();
    return () => { cancelled = true; };
  }, [availableJobs, jobIdFromParams, isEditing, selectedJob, loadingJobs]);

  useEffect(() => {
    if (isEditing && availableJobs.length > 0) {
      loadPreCon();
    }
  }, [id, availableJobs]);

  // Autosave every 10 seconds for drafts
  useEffect(() => {
    if (!isEditing || formData.status !== 'draft' || isViewOnly) return;
    // SAFETY: don't start the autosave loop until the load has finished.
    if (!formLoadedRef.current) return;

    autosaveRef.current = setInterval(() => {
      handleSaveDraft(true);
    }, 10000);

    return () => {
      if (autosaveRef.current) {
        clearInterval(autosaveRef.current);
      }
    };
  }, [isEditing, formData.status, isViewOnly]);

  const loadAvailableJobs = async () => {
    setLoadingJobs(true);
    try {
      const response = await axios.get(`${API}/api/jobs/for-field-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableJobs(response.data || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast.error('Failed to load available jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleJobSelect = (jobId) => {
    const job = availableJobs.find(j => j.id === jobId);
    if (job) {
      setSelectedJob(job);
      const teamNames = job.assigned_team_names?.join(', ') || '';
      // Auto-populate fields from job
      setFormData(prev => ({
        ...prev,
        job_id: job.id,
        job_number: job.job_number,
        site_location: job.service_address || '',
        assigned_team: teamNames,
        visit_date: job.date || todayLocal(),
        cll_lead_on_site: user?.full_name || '',
        project_name: job.job_title || '',
        client_owner: job.client_name || '',
        // Pre-fill crew with the assigned team — user can still edit it
        crew_members: prev.crew_members || teamNames,
      }));
    }
  };

  // Form is disabled until a job is selected (for new forms)
  const isFormDisabled = !isEditing && !formData.job_id;

  const loadPreCon = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await axios.get(`${API}/api/precons/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const preconData = response.data;
      
      // Find and set the selected job if editing
      if (preconData.job_id) {
        const job = availableJobs.find(j => j.id === preconData.job_id);
        if (job) {
          setSelectedJob(job);
          // Update assigned_team from current job data (not stale stored value)
          preconData.assigned_team = job.assigned_team_names?.join(', ') || preconData.assigned_team || '';
        }
      }
      
      setFormData(preconData);
      formLoadedRef.current = true;
    } catch (error) {
      // Only leave the form when the server has definitively said "no" —
      // record truly gone (404) or no permission (403). A network hiccup
      // (timeout, offline, 5xx) must NOT boot the tech back to the list.
      // Show an inline retry instead.
      const status = error.response?.status;
      if (status === 404 || status === 403) {
        toast.error(status === 404 ? 'PreCon report not found' : "You don't have permission to view this report");
        navigate('/field-reports');
      } else {
        setLoadError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = async (isAutosave = false) => {
    if (isViewOnly) return;
    // SAFETY: never PUT empty formData over a real server record before the
    // load finishes, and never un-submit an already-submitted record.
    if (isEditing && !formLoadedRef.current) return;
    if (isEditing && formData.status && formData.status !== 'draft') return;
    
    setSaving(true);
    try {
      const payload = { ...formData, status: 'draft' };
      
      let response;
      if (currentId) {
        response = await axios.put(`${API}/api/precons/${currentId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${API}/api/precons`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newId = response.data?.id;
        if (newId) {
          setCurrentId(newId);
          navigate(`/precon/${newId}`, { replace: true });
        }
      }
      
      setLastSaved(new Date());
      if (!isAutosave) {
        toast.success('Draft saved');
      }
    } catch (error) {
      if (!isAutosave) {
        toast.error('Failed to save draft');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...formData };
      
      if (currentId) {
        await axios.put(`${API}/api/precons/${currentId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const createResponse = await axios.post(`${API}/api/precons`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newId = createResponse.data?.id;
        if (newId) setCurrentId(newId);
        await axios.post(`${API}/api/precons/${newId}/submit`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('PreCon report submitted!');
        navigate(`/precon/${newId}?view=true`);
        return;
      }
      
      await axios.post(`${API}/api/precons/${currentId}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('PreCon report submitted!');
      navigate(`/precon/${currentId}?view=true`);
    } catch (error) {
      // Scroll to top + keep the toast up longer — Submit sits at the
      // bottom of a long form, so a top-right error toast can be missed
      // entirely if the tech doesn't scroll back up (2026-09-09 field
      // report: tech couldn't tell why submit "did nothing").
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (error.response?.data?.detail?.errors) {
        const errors = error.response.data.detail.errors;
        toast.error(
          <div>
            <p className="font-bold mb-1">Missing required fields:</p>
            <ul className="text-sm list-disc pl-4">
              {errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
              {errors.length > 5 && <li>...and {errors.length - 5} more</li>}
            </ul>
          </div>,
          { duration: 8000 }
        );
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail, { duration: 8000 });
      } else {
        toast.error('Failed to submit report', { duration: 8000 });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Submitter editing a report already in Pending Review — backend enforces
  // signature lock. Status never changes from this path.
  const handleSaveSubmitterEdits = async () => {
    if (!currentId) return;
    setSaving(true);
    try {
      const payload = { ...formData };
      delete payload.status;
      await axios.put(`${API}/api/precons/${currentId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastSaved(new Date());
      toast.success('Changes saved — still pending review.');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    // Guard: require a reviewer signature in state before letting them approve.
    if (!formData.reviewer_signature_url) {
      toast.error('Please sign the Manager Approval signature pad before approving.');
      return;
    }
    setApproving(true);
    try {
      // 1) Save the latest state (reviewer signature URL, reviewer name, GPS, etc.)
      //    so the backend has everything before /complete validates.
      await axios.put(`${API}/api/precons/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // 2) Call the proper /complete endpoint — it validates the reviewer signature
      //    is present and archives the final PDF.
      await axios.post(`${API}/api/precons/${id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report approved!');
      loadPreCon();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to approve report';
      toast.error(typeof msg === 'string' ? msg : 'Failed to approve report');
    } finally {
      setApproving(false);
    }
  };

  // Ensure we have a saved draft id before uploading any photos/signatures.
  // RACE LOCK to prevent duplicate drafts from rapid concurrent uploads.
  const draftCreationRef = useRef(null);
  const ensureDraftId = async () => {
    if (currentId) return currentId;
    if (draftCreationRef.current) return draftCreationRef.current;
    draftCreationRef.current = (async () => {
      try {
        const payload = { ...formData, status: 'draft' };
        const res = await axios.post(`${API}/api/precons`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newId = res.data?.id;
        if (newId) {
          setCurrentId(newId);
          navigate(`/precon/${newId}`, { replace: true });
          return newId;
        }
      } catch (err) {
        console.error('Failed to create draft for upload:', err);
        toast.error('Could not save draft before upload');
        draftCreationRef.current = null;
      }
      return null;
    })();
    return await draftCreationRef.current;
  };

  const uploadPhoto = async (file) => {
    const formId = await ensureDraftId();
    if (!formId) return null;
    try {
      const res = await uploadFieldReportPhoto('precons', formId, file, token);
      return res?.url || null;
    } catch (err) {
      console.error('Photo upload failed:', err);
      toast.error('Photo upload failed');
      return null;
    }
  };

  const uploadSignature = async (dataUrl) => {
    const formId = await ensureDraftId();
    if (!formId) return null;
    try {
      const res = await uploadFieldReportSignature('precons', formId, dataUrl, token);
      return res?.url || null;
    } catch (err) {
      console.error('Signature upload failed:', err);
      toast.error('Signature upload failed');
      return null;
    }
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const expectedAfter = (formData.scouting_photos || []).length + files.length;
    setFormData(prev => ({
      ...prev,
      expected_photos_count: Math.max(prev.expected_photos_count || 0, expectedAfter),
      photos_ready: false,
    }));
    setPendingUploads(p => p + files.length);
    if (currentId) {
      try {
        await axios.put(`${API}/api/precons/${currentId}`, {
          expected_photos_count: expectedAfter,
          photos_ready: false,
        });
      } catch (err) {
        console.warn('expected_photos_count PUT failed', err?.response?.status);
      }
    }

    setUploading(true);

    // Running accumulator — preserves the complete photo list across loop
    // iterations so each PUT sends the FULL current array. Without this,
    // React's closure pins formData.scouting_photos to its value at function
    // entry, so every PUT in a multi-photo batch overwrites the prior one
    // (root cause of the J20260521-002 blank-tile bug from commit 707ba365).
    let runningPhotos = formData.scouting_photos || [];

    for (const file of files) {
      try {
        const options = {
          // Compressed for fast load and small PDFs (see LocateForm).
          maxSizeMB: 0.3,
          maxWidthOrHeight: 1280,
          initialQuality: 0.82,
          useWebWorker: true,
          fileType: 'image/jpeg'
        };
        const compressedBlob = await imageCompression(file, options);
        const compressedFile = new File([compressedBlob], file.name, { type: compressedBlob.type || 'image/jpeg', lastModified: Date.now() });

        // Upload to OneDrive immediately; store only the URL in state
        const url = await uploadPhoto(compressedFile);
        if (!url) {
          // Silent backend failure: keep expected_photos_count in sync so
          // the reviewer doesn't see a permanent stuck banner.
          setFormData(prev => ({
            ...prev,
            expected_photos_count: Math.max(0, (prev.expected_photos_count || 1) - 1),
          }));
          setPendingUploads(p => Math.max(0, p - 1));
          toast.error(`Photo upload failed: ${file.name}. Please try again.`);
          continue;
        }
        const newPhoto = {
          id: crypto.randomUUID(),
          url,
          caption: ''
        };
        runningPhotos = [...runningPhotos, newPhoto];
        setFormData(prev => {
          const exp = Math.max(prev.expected_photos_count || 0, runningPhotos.length);
          return {
            ...prev,
            scouting_photos: runningPhotos,
            received_photos_count: runningPhotos.length,
            photos_ready: runningPhotos.length >= exp,
          };
        });
        setPendingUploads(p => Math.max(0, p - 1));
        if (currentId && formData.status !== 'draft') {
          try {
            await axios.put(`${API}/api/precons/${currentId}`, {
              scouting_photos:       runningPhotos,
              received_photos_count: runningPhotos.length,
            });
          } catch (_) {}
        }
      } catch (error) {
        console.error('Photo upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
        setPendingUploads(p => Math.max(0, p - 1));
        setFormData(prev => ({
          ...prev,
          expected_photos_count: Math.max(0, (prev.expected_photos_count || 1) - 1),
        }));
      }
    }

    setUploading(false);
    e.target.value = '';
    if (currentId) {
      try {
        await axios.put(`${API}/api/precons/${currentId}`, { photos_ready: true });
      } catch (_) {}
    }
  };

  const removePhoto = (photoId) => {
    // Optimistic local update — UI stays snappy.
    setFormData(prev => {
      const next = prev.scouting_photos.filter(p => p.id !== photoId);
      const nextExpected = Math.max(0, (prev.expected_photos_count || next.length) - 1);
      return {
        ...prev,
        scouting_photos: next,
        expected_photos_count: nextExpected,
        received_photos_count: next.length,
        photos_ready: next.length >= nextExpected,
      };
    });
    // Persist via DELETE so the backend ALSO removes the underlying
    // OneDrive file (previous code used PUT which just rewrote the array
    // and left the file orphaned — root cause of false orphans in the
    // /admin/photo-audit tool). The DELETE endpoint atomically updates
    // the array + recomputes count fields + cleans storage.
    if (currentId) {
      axios.delete(`${API}/api/precons/${currentId}/photos/${photoId}`)
        .catch(() => { /* non-fatal; UI is already updated */ });
    }
  };

  const updatePhotoCaption = (photoId, caption) => {
    setFormData(prev => ({
      ...prev,
      scouting_photos: prev.scouting_photos.map(p => 
        p.id === photoId ? { ...p, caption } : p
      )
    }));
  };

  const handleSubmitterSign = (dataUrl, gps) => {
    setFormData(prev => ({
      ...prev,
      submitter_signature_url: dataUrl,
      submitter_gps_lat: gps?.lat,
      submitter_gps_lng: gps?.lng,
      submitter_gps_accuracy: gps?.accuracy,
      submitter_signed_at: dataUrl ? new Date().toISOString() : ''
    }));
  };

  const handleReviewerSign = (dataUrl, gps) => {
    setFormData(prev => ({
      ...prev,
      reviewer_signature_url: dataUrl,
      reviewer_gps_lat: gps?.lat,
      reviewer_gps_lng: gps?.lng,
      reviewer_gps_accuracy: gps?.accuracy,
      reviewer_signed_at: dataUrl ? new Date().toISOString() : ''
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-6 text-center" data-testid="precon-load-error">
        <AlertTriangle className="w-10 h-10 text-amber-500" />
        <div>
          <p className="font-medium text-slate-900">Couldn't load this report</p>
          <p className="text-sm text-slate-500 mt-1">Check your connection and try again — your form is still there, nothing was lost.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/field-reports')} data-testid="load-error-back-btn">Back to Field Reports</Button>
          <Button onClick={loadPreCon} data-testid="load-error-retry-btn">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-[calc(11rem+env(safe-area-inset-bottom))] md:pb-32">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/field-reports')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">PreCon Site Scouting</h1>
              <p className="text-xs text-slate-500">
                {formData.job_number ? `Job: ${formData.job_number}` : 'Center Line Locating'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canAdminUnlock && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
                onClick={handleAdminEdit}
                data-testid="admin-edit-report-btn"
                title="Unlock this report to fix a mistake"
              >
                Edit Report
              </Button>
            )}
            {isAdminEditingNonDraft && (
              <Button
                size="sm"
                className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                onClick={handleAdminSaveEdits}
                disabled={saving}
                data-testid="admin-save-edits-btn"
                title="Save changes — does NOT change the report status"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            )}
            {formData.status === 'draft' && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Draft</Badge>
            )}
            {formData.status === 'submitted_pending_review' && (
              <Badge className="bg-blue-100 text-blue-700">Pending Review</Badge>
            )}
            {formData.status === 'completed' && (
              <Badge className="bg-green-100 text-green-700">Completed</Badge>
            )}
          </div>
        </div>
        {lastSaved && (
          <div className="px-4 pb-2 text-xs text-slate-400">
            Last saved: {lastSaved.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Status Banner for submitted/completed forms */}
      {(formData.status === 'submitted_pending_review' || formData.status === 'completed') && (
        <div className={`mx-4 mt-4 p-4 rounded-lg ${formData.status === 'completed' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
          <div className="flex items-center gap-3">
            {formData.status === 'completed' ? (
              <FileText className="w-6 h-6 text-green-600" />
            ) : (
              <AlertCircle className="w-6 h-6 text-blue-600" />
            )}
            <div>
              <p className={`font-semibold ${formData.status === 'completed' ? 'text-green-800' : 'text-blue-800'}`}>
                {formData.status === 'completed' ? 'Report Completed' : 'Submitted - Pending Review'}
              </p>
              <p className={`text-sm ${formData.status === 'completed' ? 'text-green-600' : 'text-blue-600'}`}>
                {formData.status === 'completed' 
                  ? `Reviewed by ${formData.reviewed_by || 'Manager'} on ${formData.reviewed_at ? new Date(formData.reviewed_at).toLocaleDateString() : 'N/A'}`
                  : `Submitted by ${formData.submitted_by || formData.cll_lead_on_site} on ${formData.submitted_at ? new Date(formData.submitted_at).toLocaleDateString() : 'N/A'}`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 space-y-6 max-w-2xl mx-auto">
        {/* Job Selection - REQUIRED FIRST — shared component, reused
            by Maintenance equipment forms (Check-In / Check-Out /
            Pre-Trip). See <JobSelectionCard>. */}
        <JobSelectionCard
          value={formData.job_id}
          onChange={handleJobSelect}
          availableJobs={availableJobs}
          loadingJobs={loadingJobs}
          selectedJob={selectedJob}
          disabled={isViewOnly || isEditing}
          testIdPrefix="precon-job-selection"
        />

        {/* Warning if no job selected */}
        {isFormDisabled && (
          <div className="bg-amber-100 border border-amber-300 rounded-lg p-4 text-center">
            <AlertCircle className="w-6 h-6 mx-auto text-amber-600 mb-2" />
            <p className="text-amber-800 font-medium">Please select a job to continue</p>
            <p className="text-sm text-amber-600">The form will be enabled once a job is selected</p>
          </div>
        )}

        {/* Section A: PreCon Information */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">A</span>
              PreCon Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Assigned Team <span className="text-slate-400 text-xs">(from Job - read only)</span></Label>
              <Input
                value={formData.assigned_team}
                placeholder="Auto-filled from job"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Site Location / Project Address <span className="text-slate-400 text-xs">(from Job - read only)</span></Label>
              <Input
                value={formData.site_location}
                placeholder="Auto-filled from job"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Date of Site Visit <span className="text-red-500">*</span> <span className="text-slate-400 text-xs">(editable)</span></Label>
              <Input
                type="date"
                value={formData.visit_date}
                onChange={(e) => updateField('visit_date', e.target.value)}
                disabled={isViewOnly}
              />
            </div>
            <div>
              <Label>CLL Lead on Site <span className="text-slate-400 text-xs">(from logged-in user)</span></Label>
              <Input
                value={formData.cll_lead_on_site}
                placeholder="Auto-filled from logged-in user"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Crew Members Present</Label>
              <Input
                value={formData.crew_members}
                onChange={(e) => updateField('crew_members', e.target.value)}
                placeholder="Names (comma-separated)"
                disabled={isViewOnly}
              />
            </div>
          </CardContent>
        </Card>

        {/* Section B: Project Details */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">B</span>
              Project Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Project Name <span className="text-slate-400 text-xs">(from Job)</span></Label>
              <Input
                value={formData.project_name}
                placeholder="Auto-filled from job"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Client / Owner <span className="text-slate-400 text-xs">(from Job)</span></Label>
              <Input
                value={formData.client_owner}
                placeholder="Auto-filled from job"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Project Number / Job ID</Label>
              <Input
                value={formData.project_number || ''}
                onChange={(e) => updateField('project_number', e.target.value)}
                placeholder="Optional reference number"
                disabled={isViewOnly}
              />
            </div>
            <div>
              <Label>Scope Overview</Label>
              <Textarea
                value={formData.scope_overview}
                onChange={(e) => updateField('scope_overview', e.target.value)}
                placeholder="Brief description of work to be done..."
                rows={3}
                disabled={isViewOnly}
              />
            </div>
            <div>
              <Label>Access Notes</Label>
              <Textarea
                value={formData.access_notes}
                onChange={(e) => updateField('access_notes', e.target.value)}
                placeholder="Gates, escorts required, working hours..."
                rows={3}
                disabled={isViewOnly}
              />
            </div>
            <div>
              <Label>Safety Notes / Hazards Observed</Label>
              <Textarea
                value={formData.safety_notes}
                onChange={(e) => updateField('safety_notes', e.target.value)}
                placeholder="Traffic, overhead lines, unstable ground..."
                rows={3}
                disabled={isViewOnly}
              />
            </div>
            <div>
              <Label>Utility Awareness Notes</Label>
              <Textarea
                value={formData.utility_awareness_notes}
                onChange={(e) => updateField('utility_awareness_notes', e.target.value)}
                placeholder="Visible markings, pedestals, valves..."
                rows={3}
                disabled={isViewOnly}
              />
            </div>
          </CardContent>
        </Card>

        {/* Section C: General Notes */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">C</span>
              General Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label>Additional PreCon Notes</Label>
              <Textarea
                value={formData.additional_notes}
                onChange={(e) => updateField('additional_notes', e.target.value)}
                placeholder="Any other observations or notes..."
                rows={4}
                disabled={isViewOnly}
              />
            </div>
          </CardContent>
        </Card>

        {/* Section D: Scouting Photos */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">D</span>
              Scouting Photos <span className="text-red-500 text-sm">*</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handlePhotoSelect}
              disabled={isViewOnly}
            />
            
            {formData.scouting_photos.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {formData.scouting_photos.map(photo => (
                  <ScoutingPhotoCard
                    key={photo.id}
                    photo={photo}
                    onRemove={() => removePhoto(photo.id)}
                    onCaptionChange={(caption) => updatePhotoCaption(photo.id, caption)}
                    disabled={isViewOnly}
                  />
                ))}
              </div>
            ) : (
              <div 
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-colors"
                onClick={() => !isViewOnly && fileInputRef.current?.click()}
              >
                <Image className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p className="text-slate-600">Tap to add photos</p>
                <p className="text-xs text-slate-400 mt-1">At least 1 photo required</p>
              </div>
            )}

            {!isViewOnly && formData.scouting_photos.length > 0 && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Add More Photos
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Section E: Signatures */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">E</span>
              Signatures & Location
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Instructions for form submission */}
            {formData.status === 'draft' && !canApproveFieldReports() && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
                <p className="font-medium mb-1">Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Sign your signature below</li>
                  <li>Click the green "Submit for Approval" button</li>
                  <li>Your manager will review and approve the form</li>
                </ol>
              </div>
            )}
            
            {/* Show rejection reason if form was rejected */}
            {formData.rejection_reason && formData.status === 'draft' && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-sm text-red-700">
                <p className="font-medium">Form was rejected:</p>
                <p>{formData.rejection_reason}</p>
                <p className="text-xs mt-1">Rejected by {formData.rejected_by} on {formData.rejected_at ? new Date(formData.rejected_at).toLocaleDateString() : 'N/A'}</p>
              </div>
            )}

            <div>
              <Label>Submitted By</Label>
              <Input
                value={formData.submitted_by}
                onChange={(e) => updateField('submitted_by', e.target.value)}
                disabled
              />
            </div>

            {/* Submitter Signature - Always visible */}
            <SignaturePad
              label="Submitter Signature"
              signatureUrl={formData.submitter_signature_url}
              onSign={handleSubmitterSign}
              gpsData={{
                lat: formData.submitter_gps_lat,
                lng: formData.submitter_gps_lng,
                accuracy: formData.submitter_gps_accuracy
              }}
              name={formData.submitter_name}
              onNameChange={(v) => updateField('submitter_name', v)}
              signedAt={formData.submitter_signed_at}
              required
              disabled={isViewOnly || formData.status !== 'draft'}
              onUploadSignature={uploadSignature}
            />

            {/* Reviewer Signature - Only visible to users with approval permission when form is submitted */}
            {(canApproveFieldReports() && formData.status === 'submitted_pending_review') && (
              <SignaturePad
                label="Manager Approval Signature"
                signatureUrl={formData.reviewer_signature_url}
                onSign={handleReviewerSign}
                gpsData={{
                  lat: formData.reviewer_gps_lat,
                  lng: formData.reviewer_gps_lng,
                  accuracy: formData.reviewer_gps_accuracy
                }}
                name={formData.reviewer_name}
                onNameChange={(v) => updateField('reviewer_name', v)}
                signedAt={formData.reviewer_signed_at}
                required={false}
                disabled={false}
                onUploadSignature={uploadSignature}
              />
            )}
            
            {/* Show reviewer signature (read-only) for completed forms */}
            {formData.status === 'completed' && formData.reviewer_signature_url && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <Label className="text-green-700">Manager Approval Signature</Label>
                <img src={formData.reviewer_signature_url} alt="Manager Signature" className="max-h-24 mt-2" />
                <p className="text-xs text-green-600 mt-1">
                  Approved by {formData.reviewer_name || formData.reviewed_by} on {formData.reviewer_signed_at ? new Date(formData.reviewer_signed_at).toLocaleDateString() : (formData.reviewed_at ? new Date(formData.reviewed_at).toLocaleDateString() : 'N/A')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fixed Bottom Actions - Only show for draft forms */}
      {!isViewOnly && formData.status === 'draft' && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <div className="px-4 pt-2 pb-1 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate"><strong>Tip:</strong> Save Draft often — Submit will also auto-save your latest changes.</span>
            </div>
            <LastSavedIndicator lastSaved={lastSaved} saving={saving} />
          </div>
          <div className="p-4 flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => handleSaveDraft(false)}
            disabled={saving}
            data-testid="save-draft-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Draft
          </Button>
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="submit-report-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Submit Report
          </Button>
          </div>
        </div>
      )}
      
      {/* Submitter Edit bar — pending-review report being edited by its
          original submitter. Approvers see the Approve/Reject bar instead. */}
      {!isViewOnly && isPending && isSubmitter && !canApproveFieldReports() && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <div className="px-4 pt-2 pb-1 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate"><strong>Pending Review</strong> — you can edit until the reviewer approves. Your signature is locked.</span>
            </div>
            <LastSavedIndicator lastSaved={lastSaved} saving={saving} />
          </div>
          <div className="p-4">
            {photosNotReady && (
              <div className="mb-3 p-2 rounded border border-amber-300 bg-amber-50 text-amber-800 text-xs flex items-start gap-2" data-testid="photo-not-ready-banner-submitter">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{photoBannerText || `${pendingUploads} photo upload${pendingUploads === 1 ? '' : 's'} still in flight — wait for them to finish before saving.`}</span>
              </div>
            )}
            <Button
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleSaveSubmitterEdits}
              disabled={saving || uploading || pendingUploads > 0}
              data-testid="submitter-save-changes-btn"
              title={pendingUploads > 0 ? 'Wait for photo uploads to finish' : ''}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {(uploading || pendingUploads > 0) ? 'Uploading photos…' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}

      {/* Admin Actions for submitted forms */}
      {canAdminEdit && formData.status === 'submitted_pending_review' && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t p-4 max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          {photosNotReady && (
            <div
              className="mb-3 p-2 rounded border border-amber-300 bg-amber-50 text-amber-800 text-xs flex items-start gap-2"
              data-testid="photo-not-ready-banner"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div>{photoBannerText || `${pendingUploads} photo upload${pendingUploads === 1 ? '' : 's'} still in flight — please wait before approving.`}</div>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/api/precons/${id}/recompute-photo-counts`, {}, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      toast.success('Photo counts reconciled');
                      const refreshed = await axios.get(`${API}/api/precons/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      setFormData(refreshed.data);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to recompute counts');
                    }
                  }}
                  className="mt-1 text-amber-900 underline font-semibold hover:text-amber-700"
                  data-testid="recompute-photo-counts-btn"
                >
                  Photos look right? → Recompute counts &amp; unlock approval
                </button>
              </div>
            </div>
          )}
          <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
            onClick={async () => {
              const reason = window.prompt('Please enter a rejection reason — the submitter will receive this in their notification and email:');
              if (reason === null) return; // User cancelled
              if (!reason.trim()) {
                toast.error('A rejection reason is required so the submitter knows what to fix.');
                return;
              }
              try {
                await axios.post(`${API}/api/precons/${id}/reject?rejection_reason=${encodeURIComponent(reason)}`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Report rejected — submitter notified by email and in-app.');
                navigate('/field-reports');
              } catch (error) {
                toast.error('Failed to reject report');
              }
            }}
            data-testid="reject-report-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700"
            onClick={handleApprove}
            disabled={approving || photosNotReady}
            data-testid="approve-report-btn"
            title={photosNotReady ? 'Photos still uploading — wait until all are received.' : ''}
          >
            {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Approve Report
          </Button>
          </div>
        </div>
      )}
      
      {/* Generate PDF Button for completed forms (non-admin or already completed) */}
      {(isViewOnly && !canAdminEdit) && (formData.status === 'submitted_pending_review' || formData.status === 'completed') && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t p-4 flex gap-3 max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <Button
            className="flex-1 bg-orange-600 hover:bg-orange-700"
            onClick={() => navigate(`/precon/${id}/preview`)}
            data-testid="print-preview-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Print / Save PDF
          </Button>
        </div>
      )}

      {/* PDF Button for completed forms - show to admin after approval */}
      {formData.status === 'completed' && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t p-4 flex gap-3 max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <Button
            className="flex-1 bg-orange-600 hover:bg-orange-700"
            onClick={() => navigate(`/precon/${id}/preview`)}
            data-testid="print-preview-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Print / Save PDF
          </Button>
        </div>
      )}
    </div>
  );
}
